/**
 * Keeping one statement from being counted twice. Rows are keyed by the Drive
 * file they came from, so a second copy of a statement (downloaded twice,
 * uploaded through the page and also dropped in Drive) would otherwise add
 * every transaction and holding again. Three layers, each a pure function:
 *
 * 1. `sameFile`: Drive's md5 matches a document already read → skip before
 *    calling Claude.
 * 2. `sameStatement`: different bytes, but the same institution, accounts,
 *    type and period end as a document already read → skip after reading.
 * 3. `dedupeTransactions` / `oneHoldingsSource`: the analysis counts a row found
 *    in two documents once, which also covers overlapping statements.
 *
 * See ARCHITECTURE.md, Duplicates.
 */
import type { Extraction } from './extraction'
import { accountKey } from './filing'
import type { Row } from './google/sheets'

export const DUPLICATES = 'Duplicates'

/** Marks a row as a linked Tiller sheet's in source_file_id; no Drive file id looks like this. See tiller.ts. */
export const TILLER_SOURCE = 'tiller'

/** Statuses whose rows are in the Sheet; a duplicate of one of these adds nothing. */
const COUNTED = new Set(['done', 'review'])

/** Statement types where one institution + accounts + period end means one document. */
const STATEMENTS = new Set(['bank_statement', 'credit_card_statement', 'brokerage_statement', 'retirement_statement', 'loan_statement'])

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
/** Matched by bank and last 4, so an account filed under two spellings of its id is one account. */
const accountSet = (ids: string) =>
  [
    ...new Set(
      ids
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map(accountKey),
    ),
  ]
    .sort()
    .join(',')

/**
 * An earlier document with the same bytes whose rows are in the Sheet. One
 * Claude couldn't read (Needs Review with no doc_type: too long, refused, an
 * unsupported format) wrote no rows, so a fresh copy of it is read, not set aside.
 */
export function sameFile(docs: Row[], fileId: string, md5: string | undefined): Row | undefined {
  if (!md5) return undefined
  return docs.find((d) => str(d.file_id) !== fileId && str(d.md5) === md5 && COUNTED.has(str(d.status)) && str(d.doc_type))
}

export function sameStatement(docs: Row[], fileId: string, x: Extraction, accountIds: (string | undefined)[]): Row | undefined {
  if (!STATEMENTS.has(x.doc_type) || !x.document_date) return undefined
  const accounts = accountSet(accountIds.filter(Boolean).join(','))
  if (!accounts) return undefined
  return docs.find(
    (d) =>
      str(d.file_id) !== fileId &&
      COUNTED.has(str(d.status)) &&
      str(d.doc_type) === x.doc_type &&
      str(d.period_end) === x.document_date &&
      // Not the institution's text: it follows the Accounts tab, which the
      // person can rename after earlier documents were filed.
      accountSet(str(d.account_id)) === accounts,
  )
}

/**
 * The same transaction found in more than one document — a monthly statement
 * and the annual statement covering it — is kept from one document only.
 * Rows match on account, date and amount, not description: two statements
 * often word one transaction differently, while two different transactions
 * on one account, day and amount almost never come from two documents.
 * Within a single document such rows are real — two $5 coffees on one day —
 * so the document with the most copies of a row decides how many are kept.
 */
export function dedupeTransactions(rows: Row[]): Row[] {
  // Tiller rows were matched against these before they were added (tiller.ts),
  // one for one; counting them here again would drop a real second purchase.
  const tiller = rows.filter((r) => str(r.source_file_id) === TILLER_SOURCE)
  if (tiller.length) return [...dedupeTransactions(rows.filter((r) => str(r.source_file_id) !== TILLER_SOURCE)), ...tiller]
  const key = (r: Row) => [str(r.account_id), str(r.date), Number(r.amount).toFixed(2)].join('|')
  const perSource = new Map<string, Map<string, number>>()
  for (const r of rows) {
    const k = key(r)
    const counts = perSource.get(k) ?? new Map<string, number>()
    counts.set(str(r.source_file_id), (counts.get(str(r.source_file_id)) ?? 0) + 1)
    perSource.set(k, counts)
  }
  const keep = new Map<string, string>()
  for (const [k, counts] of perSource) {
    const [source] = [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]
    keep.set(k, source)
  }
  return rows.filter((r) => keep.get(key(r)) === str(r.source_file_id))
}

/** Document statuses whose rows may be half written: filing failed partway, or a tab closed mid-run. */
const UNFINISHED = new Set(['error', 'processing'])

/**
 * The tables without the rows of documents that didn't finish filing. Such a
 * document stays in the drop zone and the next run replaces its rows; until
 * then, a balance without that month's transactions would skew every figure.
 */
export function withoutUnfinished<T extends { documents: Row[]; balances: Row[]; transactions: Row[]; holdings: Row[] }>(t: T): T {
  const unfinished = new Set(t.documents.filter((d) => UNFINISHED.has(str(d.status))).map((d) => str(d.file_id)))
  if (!unfinished.size) return t
  const keep = (r: Row) => !unfinished.has(str(r.source_file_id))
  return { ...t, balances: t.balances.filter(keep), transactions: t.transactions.filter(keep), holdings: t.holdings.filter(keep) }
}

/**
 * One account filed under two ids by an earlier build (fidelity-brokerage-2898
 * and fidelity-checking-2898: the same bank and last 4) is read as one, under
 * the id listed first in Accounts. Otherwise each id keeps its own latest
 * balance and net worth counts the account twice, and a transaction filed
 * under both ids is not recognised as one.
 */
export function mergeSplitAccounts<T extends { accounts: Row[]; balances: Row[]; transactions: Row[]; holdings: Row[] }>(t: T): T {
  const first = new Map<string, Row>()
  const rename = new Map<string, string>()
  const accounts: Row[] = []
  for (const a of t.accounts) {
    const id = str(a.account_id)
    const key = accountKey(id)
    // An id with no last 4 (a 401(k) plan, an Apple Card) is its own key.
    if (key === id.trim().toLowerCase()) {
      accounts.push(a)
      continue
    }
    const kept = first.get(key)
    if (!kept) {
      const copy = { ...a }
      first.set(key, copy)
      accounts.push(copy)
      continue
    }
    rename.set(id, str(kept.account_id))
    if (!str(kept.closed) && str(a.closed)) kept.closed = a.closed
  }
  if (!rename.size) return t
  const fix = (r: Row) => (rename.has(str(r.account_id)) ? { ...r, account_id: rename.get(str(r.account_id))! } : r)
  return { ...t, accounts, balances: t.balances.map(fix), transactions: t.transactions.map(fix), holdings: t.holdings.map(fix) }
}

/**
 * Adds `txn_key` to each transaction: account, date, amount and which
 * occurrence it is within its own document (the second $5 coffee that day is
 * #2). Unlike txn_id, which is the row's position in Claude's output, the key
 * survives a re-read and matches the same transaction in another document, so
 * a correction made against one copy applies to whichever copy is counted.
 */
export function withTxnKeys(rows: Row[]): Row[] {
  const position = (r: Row) => Number(/:(\d+)$/.exec(str(r.txn_id))?.[1] ?? 0)
  const seen = new Map<string, number>()
  const order = rows.map((r, i) => ({ r, i })).sort((a, b) => position(a.r) - position(b.r) || a.i - b.i)
  const keys = new Map<Row, string>()
  for (const { r } of order) {
    const base = [str(r.account_id), str(r.date), Number(r.amount).toFixed(2)].join('|')
    const n = (seen.get(`${str(r.source_file_id)}|${base}`) ?? 0) + 1
    seen.set(`${str(r.source_file_id)}|${base}`, n)
    keys.set(r, `${base}|${n}`)
  }
  return rows.map((r) => ({ ...r, txn_key: keys.get(r)! }))
}

/**
 * Holdings for one account on one date come from a single document: if two
 * documents report the same account and date, the one listing more holdings
 * (then the earlier id, for stability) is used.
 */
export function oneHoldingsSource(rows: Row[]): Row[] {
  const sources = new Map<string, Map<string, number>>()
  for (const h of rows) {
    const k = `${str(h.account_id)}|${str(h.as_of)}`
    const counts = sources.get(k) ?? new Map<string, number>()
    counts.set(str(h.source_file_id), (counts.get(str(h.source_file_id)) ?? 0) + 1)
    sources.set(k, counts)
  }
  const chosen = new Map([...sources].map(([k, counts]) => [k, [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0]]))
  return rows.filter((h) => chosen.get(`${str(h.account_id)}|${str(h.as_of)}`) === str(h.source_file_id))
}
